import { AtsDateUnit } from '../models';

const NullHigh = new Date('12/12/4200');
const NullLow = new Date('01/01/1970');
const NullCustom = (days: number, fromHigh: boolean = true): Date => {
	const dt = fromHigh ? NullHigh : NullLow;
	return new Date(dt.setDate(dt.getDate() + days));
};

export class AtsDate {
	public static Null = {
		HIGH: NullHigh,
		LOW: NullLow,
		STR: {
			HIGH: NullHigh.toISOString(),
			LOW: NullLow.toISOString(),
			CUSTOM: (days: number, fromHigh: boolean = true) => NullCustom(days, fromHigh).toISOString(),
		},

		CUSTOM: NullCustom,
	};
	/**
	 * Get the current date and time
	 */
	public static Now = new Date();
	/**
	 * Get the current date at midnight
	 */
	public static Today = new Date(new Date().setHours(0, 0, 0, 0));

	public static clone = (dt: Date = new Date()) => {
		return new Date(dt);
	};

	public static addSeconds = (by: number, to: Date = new Date()): Date => AtsDate.add(by, to, AtsDateUnit.Second);

	public static subtractSeconds = (by: number, to: Date = new Date()): Date =>
		AtsDate.subtract(by, to, AtsDateUnit.Second);

	public static addDays = (by: number, to: Date = new Date()): Date => AtsDate.add(by, to, AtsDateUnit.Day);

	public static addMinutes = (by: number, to: Date = new Date()): Date => AtsDate.add(by, to, AtsDateUnit.Minute);

	public static subtractDays = (by: number, to: Date = new Date()): Date => AtsDate.subtract(by, to, AtsDateUnit.Day);

	public static addMonths = (by: number, to: Date = new Date()): Date => AtsDate.add(by, to, AtsDateUnit.Month);

	public static subtractMonths = (by: number, to: Date = new Date()): Date =>
		AtsDate.subtract(by, to, AtsDateUnit.Month);

	public static add = (by: number, to?: Date, unit?: AtsDateUnit) => {
		const retVal = AtsDate.clone(to || new Date());
		switch (unit) {
			case AtsDateUnit.Second:
				return new Date(retVal.setSeconds(retVal.getSeconds() + by));
			case AtsDateUnit.Minute:
				return new Date(retVal.setMinutes(retVal.getMinutes() + by));
			case AtsDateUnit.Hour:
				return new Date(retVal.setHours(retVal.getHours() + by));
			case AtsDateUnit.Day:
				return new Date(retVal.setDate(retVal.getDate() + by));
			case AtsDateUnit.Month:
				return new Date(retVal.setMonth(retVal.getMonth() + by));
			case AtsDateUnit.Year:
				return new Date(retVal.setFullYear(retVal.getFullYear() + by));
			default:
				return new Date(retVal.getMilliseconds() + by);
		}
	};

	public static subtract = (by: number, to?: Date, unit?: AtsDateUnit) => new Date(AtsDate.add(by * -1, to, unit));

	public static since = (dt: Date, unit: AtsDateUnit) => {
		const then = AtsDate.clone(dt);
		const diff = AtsDate.Now.getTime() - then.getTime();
		return AtsDate.fromMS(diff, unit);
	};

	public static isPast = (dt: Date): boolean => AtsDate.since(dt, AtsDateUnit.Second) > 0;
	public static isBefore = (dt: Date): boolean => AtsDate.since(dt, AtsDateUnit.Second) < 0;

	public static formatDuration = (num: number, unit: AtsDateUnit) =>
		AtsDate.formatDurationFromMS(AtsDate.toMS(num, unit));

	public static formatDurationFromMS = (num: number) => {
		const hours = Math.floor(AtsDate.fromMS(num, AtsDateUnit.Hour));
		num = num - AtsDate.toMS(hours, AtsDateUnit.Hour);
		const minutes = Math.floor(AtsDate.fromMS(num, AtsDateUnit.Minute));
		num = num - AtsDate.toMS(minutes, AtsDateUnit.Minute);
		const seconds = Math.floor(AtsDate.fromMS(num, AtsDateUnit.Second));
		num = num - AtsDate.toMS(seconds, AtsDateUnit.Second);
		// prettier-ignore
		return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
	};

	public static toMS = (num: number, unit: AtsDateUnit) => {
		switch (unit) {
			case AtsDateUnit.Second:
				return num * 1000;
			case AtsDateUnit.Minute:
				return num * 1000 * 60;
			case AtsDateUnit.Hour:
				return num * 1000 * 60 * 60;
			default:
				return num;
		}
	};

	public static fromMS = (num: number, unit: AtsDateUnit) => {
		switch (unit) {
			case AtsDateUnit.Second:
				return num / 1000;
			case AtsDateUnit.Minute:
				return num / (1000 * 60);
			case AtsDateUnit.Hour:
				return num / (1000 * 60 * 60);
			case AtsDateUnit.Day:
				return num / (1000 * 60 * 60 * 24);
			default:
				return num;
		}
	};

	public static until = (dt: Date, unit: AtsDateUnit) => AtsDate.since(dt, unit) * -1;

	public static toMidnight = (dateTime: Date): Date => new Date(dateTime.setHours(0, 0, 0, 0));
}
